/**
 * Chat Shell Helpers
 *
 * Build merged chat lists for the current workspace shell.
 *
 * Primary responsibilities:
 * - Version workspace chat-shell cache keys
 * - Merge workspace chats with joined group chats
 * - Dedupe and sort chats by recency
 *
 * Dependencies:
 * - Supabase generated chat row types
 */

import { Tables } from '@/supabase/types'

type ChatRow = Tables<'chats'>

// v4: Space-scoped chats belong in the shell list (`{emoji} / {name}`).
const CHAT_SHELL_CACHE_VERSION = 'v4'

// --------------

function getChatTimestamp(chat: Pick<ChatRow, 'updated_at' | 'created_at'>) {
  const timestamp = Date.parse(chat.updated_at ?? chat.created_at ?? '')
  return Number.isFinite(timestamp) ? timestamp : Number.NEGATIVE_INFINITY
}

// --------------

export const buildChatShellCacheKey = (workspaceId: string, userId: string) =>
  `chat_shell_${CHAT_SHELL_CACHE_VERSION}_${workspaceId}_${userId}`

// --------------

export const mergeChatShellLists = (...chatGroups: ChatRow[][]) => {
  const mergedChats = new Map<string, ChatRow>()

  chatGroups.flat().forEach((chat) => {
    const existingChat = mergedChats.get(chat.id)

    if (!existingChat || getChatTimestamp(chat) >= getChatTimestamp(existingChat)) {
      mergedChats.set(chat.id, chat)
    }
  })

  return Array.from(mergedChats.values()).sort(
    (left, right) => getChatTimestamp(right) - getChatTimestamp(left)
  )
}
